// ConfirmationDialog.tsx
import React, { createContext, useContext, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button, ButtonProps } from '@/components/ui/button.tsx'

type ButtonVariant = ButtonProps['variant']

type ConfirmOptions = {
  title?: React.ReactNode
  content?: React.ReactNode
  icon?: React.ReactNode
  onOk?: () => void | Promise<void>
  onCancel?: () => void | Promise<void>
  okText?: React.ReactNode
  cancelText?: React.ReactNode
  okButtonVariant?: ButtonVariant
  cancelButtonVariant?: ButtonVariant
}

const ConfirmContext = createContext<{
  confirm: (options: ConfirmOptions) => void
}>({
  confirm: () => {},
})

let confirmInstance: ((options: ConfirmOptions) => void) | null = null

function registerConfirm(confirm: (options: ConfirmOptions) => void) {
  confirmInstance = confirm
}

export function confirm(options: ConfirmOptions) {
  if (!confirmInstance) {
    throw new Error(
      'Confirmation dialog not initialized. Make sure to wrap your app with ConfirmProvider.'
    )
  }
  return confirmInstance(options)
}

export const useConfirm = () => useContext(ConfirmContext)

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean
    options: ConfirmOptions
  }>({
    isOpen: false,
    options: {},
  })

  const [isOkLoading, setIsOkLoading] = useState(false)
  const [isCancelLoading, setIsCancelLoading] = useState(false)

  const confirm = (options: ConfirmOptions) => {
    setConfirmState({
      isOpen: true,
      options,
    })
  }

  useEffect(() => {
    registerConfirm(confirm)
  }, [])

  const handleClose = () => {
    // Only allow closing if no loading states are active
    if (isOkLoading || isCancelLoading) return

    setConfirmState((prev) => ({
      ...prev,
      isOpen: false,
    }))
  }

  const handleConfirm = async (e: React.MouseEvent) => {
    e.preventDefault() // Prevent default form submission

    if (confirmState.options.onOk) {
      try {
        setIsOkLoading(true)
        await Promise.resolve(confirmState.options.onOk())
        // Only close after the promise is resolved
        handleClose()
      } catch (error) {
        console.error('Error in onOk callback:', error)
      } finally {
        setIsOkLoading(false)
      }
    } else {
      handleClose()
    }
  }

  const handleCancel = async (e: React.MouseEvent) => {
    e.preventDefault() // Prevent default form submission

    if (confirmState.options.onCancel) {
      try {
        setIsCancelLoading(true)
        await Promise.resolve(confirmState.options.onCancel())
        // Only close after the promise is resolved
        handleClose()
      } catch (error) {
        console.error('Error in onCancel callback:', error)
      } finally {
        setIsCancelLoading(false)
      }
    } else {
      handleClose()
    }
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog
        open={confirmState.isOpen}
        onOpenChange={(open) => {
          // Only allow closing via onOpenChange if no loading states are active
          if (!open && !isOkLoading && !isCancelLoading) {
            handleClose()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirmState.options.icon}
              {confirmState.options.title}
            </AlertDialogTitle>
            {confirmState.options.content && (
              <AlertDialogDescription>
                {confirmState.options.content}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              onClick={handleCancel}
              disabled={isOkLoading || isCancelLoading}
              variant={confirmState.options.cancelButtonVariant ?? 'outline'}
            >
              {isCancelLoading && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              {confirmState.options.cancelText || 'Cancel'}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isOkLoading || isCancelLoading}
              type="button"
              variant={confirmState.options.okButtonVariant ?? 'default'}
            >
              {isOkLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {confirmState.options.okText || 'OK'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  )
}
